/* eslint-disable @typescript-eslint/no-explicit-any */
import apiService from "./api";
import { sanitizeQueryParams } from "../utility/helpers/apiHelper";

export interface AppUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  userName?: string | null;
  dateOfBirth?: string | null;
  email: string;
  phoneNumber: string | null;
  status: string;
  role?: string | null;
  isEmailVerified?: boolean | number;
  stripeCustomerId?: string | null;
  createdAt: string;
  updatedAt?: string;
  displayName?: string;
  businessUserId?: number;
  businessId?: number;
  business_id?: number;
  businessName?: string | null;
  businessEmail?: string | null;
  businessPhone?: string | null;
  businessWebsite?: string | null;
  businessDescription?: string | null;
  businessLogoUrl?: string | null;
  verificationStatus?: string | null;
}

export interface LinkedBusiness {
  businessUserId: number;
  businessId: number;
  linkedAt: string;
  businessName: string;
  description?: string | null;
  email?: string | null;
  phone?: string | null;
  logoUrl?: string | null;
  website?: string | null;
  verificationStatus?: string | null;
}

export interface AppUserBooking {
  bookingId: number;
  businessId: number;
  addOns?: number;
  time?: string | null;
  locationType?: string | null;
  bookingStatus?: string | null;
  paymentStatus?: string | null;
  totalAmount?: string | number | null;
  duration?: number | null;
  createdAt?: string | null;
}

export interface UserPost {
  id?: number;
  postId?: number;
  businessId?: number | null;
  postType?: string | null;
  title?: string | null;
  content?: string | null;
  visibility?: string | null;
  tags?: string | null;
  mentions?: string | null;
  likeCount?: number;
  commentCount?: number;
  viewCount?: number;
  media?: string[];
  status?: string | null;
  createdAt?: string | null;
  [key: string]: unknown;
}

export interface AppUserDetails {
  user: AppUser;
  bookings: AppUserBooking[];
  posts: UserPost[];
}

export interface BusinessUserDetails {
  user: AppUser;
  businesses: LinkedBusiness[];
  posts: UserPost[];
}

export type CustomerUserDetails = AppUserDetails | BusinessUserDetails;

export const isBusinessUserDetails = (
  data: AppUser | CustomerUserDetails
): data is BusinessUserDetails => "user" in data && "businesses" in data;

export const isAppUserDetails = (
  data: AppUser | CustomerUserDetails
): data is AppUserDetails => "user" in data && "bookings" in data;

export interface AppUserByRole {
  userId: number;
  email: string;
  role: string;
}

export interface GetAppUsersParams {
  page?: number;
  pageSize?: number;
  limit?: number;
  search?: string;
  status?: string;
  orderBy?: string;
  orderDirection?: string;
  [key: string]: any;
}

export interface CreateAppUserBody {
  firstName: string;
  lastName: string;
  email: string;
  userName?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  password?: string;
  status?: string;
}

export interface UpdateAppUserBody {
  firstName?: string;
  lastName?: string;
  email?: string;
  userName?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  password?: string;
  status?: string;
}

const toCustomerListParams = (query: GetAppUsersParams = {}) =>
  sanitizeQueryParams({
    page: query.page,
    limit: query.pageSize ?? query.limit,
    search: query.search,
    status: query.status,
    orderBy: query.orderBy,
    orderDirection: query.orderDirection?.toLowerCase(),
  });

export class CustomerService {
  async getAppUsers(query?: GetAppUsersParams) {
    return apiService.get("/customers/app-users", {
      params: toCustomerListParams(query),
    });
  }

  async getBusinessUsers(query?: GetAppUsersParams) {
    return apiService.get("/customers/business-users", {
      params: toCustomerListParams(query),
    });
  }

  async getBusinessUsersDetails(id: string | number) {
    return apiService.get(`/customers/business-users/${id}`);
  }

  /** Distinct roles from app_users.role */
  async getAppUserRoles() {
    return apiService.get("/customers/app-users/roles");
  }

  /** Users (userId + email) for a given app_users.role */
  async getAppUsersByRole(role: string) {
    return apiService.get("/customers/app-users/by-role", {
      params: { role },
    });
  }

  async getAppUser(id: string | number) {
    return apiService.get(`/customers/app-users/${id}`);
  }

  async createAppUser(body: CreateAppUserBody) {
    return apiService.post("/customers", body);
  }

  async updateAppUser(id: string | number, body: UpdateAppUserBody) {
    return apiService.put(`/customers/app-users/${id}`, body);
  }

  async updateAppUserStatus(id: string | number, status: string) {
    return apiService.patch(`/customers/app-users/${id}/status`, { status });
  }

  async deleteAppUser(id: string | number) {
    return apiService.delete(`/customers/app-users/${id}`);
  }
}

export const customerService = new CustomerService();
